import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { DataFlowColumn } from 'app/model/dataset/dataflowcolumn';

import { ExpressionBasedColumn } from './expression-based.column';
import { Owned } from '../base/named';
import { format } from '../../shared/utils/string.util';
import { LongDataFlowType } from './dataflow-datatype';
import { SingleStreamOutputTransformation } from './single-output-stream.transformation';
import { keyTransformationOutputColumns, validateColumn, IKeyColumnTransformation, getInspectSummaryOutputFields, getInspectDetailsOutputFields } from './keytransformation.utils';
import { DataFlowSource } from './dataflow-source';
import { isTruthy } from './dataflow-expression-utilities';
import { GlobalVariables } from 'app/services/globals';

@Metamodel.model({
    name: 'keyGenerate',
    display: [stringResources.DataFlowSurrogateKeyTransformationLabel, stringResources.DataFlowSurrogateKeyTransformationsLabel],
    referable: true,
    constraints: {
        name: {
            required: true
        }
    }
})
export class SurrogateKeyTransformation extends SingleStreamOutputTransformation implements IKeyColumnTransformation {

    public static readonly MODEL: IModel;
    // LONG in scala is 9223372036854775807L, should we use that?
    public static readonly MAX_SURROGATE_VALUE = Number.MAX_SAFE_INTEGER;

    public get columns(): DataFlowColumn[] {
        return [this.keyColumn];
    }

    @Metamodel.attribute({
        type: ExpressionBasedColumn.MODEL,
        constraint: {
            required: true,
            validator: validateColumn
        }
    })
    public keyColumn: DataFlowColumn = Owned.create<DataFlowColumn>(DataFlowColumn.MODEL, this);

    @Metamodel.attribute({
        display: stringResources.DataFlowSurrogateKeyStartValueModel,
        constraint: {
            minimum: 1,
            maximum: SurrogateKeyTransformation.MAX_SURROGATE_VALUE
        }
    })
    public startAtValue: number = 1;

    @Metamodel.attribute({
        display: stringResources.DataFlowSurrogateKeyStepValueModel,
        constraint: {
            minimum: 1,
            maximum: SurrogateKeyTransformation.MAX_SURROGATE_VALUE
        }
    })
    public stepValue: number = 1;

    @Metamodel.attribute({})
    public enableCdc: boolean = false;

    public newColumn: boolean = true;
    public inspectSummaryOutputFields = getInspectSummaryOutputFields(this);
    public inspectDetailsOutputFields = getInspectDetailsOutputFields(this);

    public showEnableCdc(): boolean {
        const upstreamCdcSources = this.getAllUpTransformations().filter((tx) => tx instanceof DataFlowSource)
            .filter((source) => this.isSourceCdcEnabled(source as DataFlowSource));
        return (GlobalVariables.isStatefulSurrogateKeyEnabled && upstreamCdcSources && (upstreamCdcSources.length > 0));
    }

    public constructor() {
        super();
        this.keyColumn.dataFlowColumnType = new LongDataFlowType();
    }

    protected getGeneratedDescription(): string {
        if (this.keyColumn && this.keyColumn.name) {
            return format(stringResources.SurrogateKeyDescription, this.keyColumn.name, this.startAtValue.toString(), this.stepValue.toString());
        }

        return format(stringResources.SurrogateKeyNoColumnDescription);
    }

    protected _outputColumns(): DataFlowColumn[] {
        return keyTransformationOutputColumns(this, this.inputColumns);
    }

    private isSourceCdcEnabled(source: DataFlowSource): boolean {
        return (isTruthy(source.enableCdc) || isTruthy(source.enableChangeFeed) || isTruthy(source.enableNativeCdc));
    }
}
